// The Verdict band: the topline conclusion, pinned above the tab bar.
//
// This is the Gen Z home's Weekly Dispatch band, ported verbatim (user
// 2026-08-21: "the genz dashboard home... weekly dispatch looks same exact
// so cant u copy the design and elements and UI and animations"): the same
// two angled SVG shapes (#506ED3 face over a #749ACC sliver) that DRAW
// themselves in once when the band scrolls into view - stroke traces the
// outline, the fill fades in, the stroke fades away - then the side text
// fades up. Same side-column typography (Inter uppercase label, hairline,
// scope line), same content typography (Space Grotesk headline, Inter lede).
//
// It sits above the tabs and outside them on purpose, so a reader who has
// navigated to Voices still has the conclusion in view when a striking
// quote appears. The `status` chip is an analyst judgement and stays
// optional rather than being invented per project.
import React from "react";
import styled, { keyframes } from "styled-components";
import dotsPattern from "../assets/genz/gzr_dots_pattern.svg";
import dotsPatternMask from "../assets/genz/gzr_dots_pattern_mask.svg";
import { FONT, FIG_INK } from "./tokens";
import { useInViewOnce } from "./motion";
import { stripDashes } from "./text";

// Gen Z dispatch band colors, copied exactly (V6 GZRDispatchBand).
const FACE = "#506ED3";
const BACK = "#749ACC";
const CARD_SHADOW = "0px 5px 60px rgba(84, 90, 129, 0.15)";

const dotsReveal = keyframes`
  from { clip-path: circle(0% at 50% 50%); opacity: 0; transform: scale(0.96); }
  to { clip-path: circle(78% at 50% 50%); opacity: 1; transform: scale(1); }
`;
const dotsSweep = keyframes`
  0%, 4% { transform: translateX(170%); opacity: 0; }
  10% { opacity: 0.9; }
  25% { opacity: 0.5; }
  40% { opacity: 0.12; }
  46%, 100% { transform: translateX(-70%); opacity: 0; }
`;
const dotsBurst = keyframes`
  0%, 66% { transform: scale(0.15); opacity: 0; }
  70% { opacity: 0.85; }
  82% { opacity: 0.4; }
  92%, 100% { transform: scale(2.3); opacity: 0; }
`;

// The Gen Z home's right-side dots animation (GzrDotsLoopDecor), copied
// verbatim: the dot pattern reveals once, then a soft glow sweeps through
// the dots and a corner burst breathes, on the same 28s loop.
const DotsLoop = styled.div`
  position: absolute;
  top: 0;
  right: -9.05%;
  width: max(29.85%, 230px);
  aspect-ratio: 394 / 383;
  transform: translateY(-39.7%);
  pointer-events: none;
  .dots-reveal {
    position: absolute;
    inset: 0;
    clip-path: circle(0% at 50% 50%);
    opacity: 0;
    transform: scale(0.96);
  }
  &.in .dots-reveal { animation: ${dotsReveal} 1.8s ease both; }
  .dots-base {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }
  .dots-glints {
    position: absolute;
    inset: 0;
    overflow: hidden;
    filter: blur(3px);
    -webkit-mask-image: url(${dotsPatternMask});
    mask-image: url(${dotsPatternMask});
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
  }
  .dots-sweep {
    position: absolute;
    top: -25%;
    bottom: -25%;
    left: 0;
    width: 120%;
    background: radial-gradient(
      ellipse at center,
      #2e45dade 0%,
      #506ed38c 50%,
      transparent 82%
    );
    animation: ${dotsSweep} 28s ease-in-out infinite;
  }
  .dots-burst {
    position: absolute;
    left: -30%;
    bottom: -30%;
    width: 100%;
    aspect-ratio: 1;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      #2e45dade 0%,
      #506ed380 50%,
      transparent 80%
    );
    animation: ${dotsBurst} 28s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .dots-reveal { animation: none; clip-path: circle(78% at 50% 50%); opacity: 1; transform: none; }
    .dots-sweep, .dots-burst { animation: none; opacity: 0; }
  }
`;

const Band = styled.section`
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: ${CARD_SHADOW};
  overflow: hidden;
  /* Figma 7102:10742: 214 tall. */
  min-height: 214px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  font-family: ${FONT};

  .band-back,
  .band-front {
    position: absolute;
    top: 0;
    height: 100%;
    width: 311px;
    pointer-events: none;
  }
  .band-back { left: -93px; }
  .band-front { left: -77px; }

  .band-side {
    position: absolute;
    left: 35px;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 17px;
    z-index: 1;
  }
  .band-label {
    font-family: ${FONT};
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #ffffff;
    text-transform: uppercase;
  }
  .band-line {
    width: 80px;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
  }
  .band-scope {
    font-family: ${FONT};
    font-weight: 700;
    font-size: 12px;
    line-height: 18px;
    color: #ffffff;
    text-transform: uppercase;
    max-width: 170px;
  }
  .band-status {
    margin-top: 12px;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-family: ${FONT};
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    color: #fdbc24;
    text-transform: uppercase;
  }
  .band-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fdbc24;
    box-shadow: 0 0 0 2px rgba(253, 188, 36, 0.2);
  }

  .band-content {
    margin-left: 250px;
    max-width: 902px;
    padding: 28px 40px 28px 0;
    position: relative;
    flex: 1;
    z-index: 1;
  }
  .band-headline {
    font-family: ${FONT};
    font-weight: 700;
    font-size: 22px;
    line-height: 30px;
    color: ${FIG_INK};
    text-wrap: balance;
    margin: 0;
  }
  .band-lede {
    margin: 14px 0 0;
    font-family: ${FONT};
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    color: ${FIG_INK};
    max-width: 78ch;
    b { font-weight: 700; }
  }

  /* Small screens: keep the banner, scale it down (the Gen Z rule). */
  @media (max-width: 900px) {
    min-height: 180px;
    .band-back, .band-front { width: 232px; }
    .band-back { left: -70px; }
    .band-front { left: -58px; }
    .band-side { left: 24px; gap: 12px; }
    .band-label { font-size: 18px; line-height: 24px; }
    .band-line { width: 60px; }
    .band-scope { font-size: 10px; line-height: 15px; max-width: 130px; }
    .band-status { font-size: 10px; line-height: 15px; margin-top: 8px; gap: 8px; }
    .band-content { margin-left: 186px; padding: 20px 24px 20px 0; }
  }
  @media (max-width: 575.99px) {
    .band-headline { font-size: 16px; line-height: 22px; }
  }
`;

/**
 * @param {string} title    left panel heading, e.g. "The Verdict"
 * @param {string} scope    e.g. "43 interviews · 21 states"
 * @param {string} status   optional confidence chip; omitted unless supplied
 * @param {string} headline the one-sentence conclusion
 * @param {node}   children the supporting paragraph
 */
export default function VerdictBand({
  title = "The Verdict",
  scope,
  status,
  headline,
  children,
}) {
  const [revealRef, inView] = useInViewOnce();
  if (!headline && !children) return null;

  // The Gen Z dispatch draw-in: trace the outline, fade the fill in, fade
  // the stroke away. Delay staggers the back shape behind the face.
  const draw = (fill, delay) =>
    inView
      ? {
          fill,
          fillOpacity: 1,
          stroke: fill,
          strokeWidth: 2,
          strokeDasharray: 1,
          strokeDashoffset: 0,
          strokeOpacity: 0,
          transition:
            `stroke-dashoffset 2s cubic-bezier(.22,1,.36,1) ${delay}s, ` +
            `fill-opacity 1s ease ${delay + 1.1}s, ` +
            `stroke-opacity 0.4s ease ${delay + 1.8}s`,
        }
      : {
          fill,
          fillOpacity: 0,
          stroke: fill,
          strokeWidth: 2,
          strokeDasharray: 1,
          strokeDashoffset: 1,
          strokeOpacity: 1,
        };
  const textFade = {
    opacity: inView ? 1 : 0,
    transition: "opacity 0.8s ease 1.5s",
  };

  return (
    <Band ref={revealRef}>
      {/* Paint order per the Gen Z figma: the muted shape UNDER (a sliver
          peeks past the diagonal edge), the face shape ON TOP. */}
      <svg className="band-front" viewBox="0 0 311 234" preserveAspectRatio="none" aria-hidden>
        <path d="M256 0H0V234H288.9L311 195.722L256 0Z" pathLength="1" style={draw(BACK, 0)} />
      </svg>
      <svg className="band-back" viewBox="0 0 311 234" preserveAspectRatio="none" aria-hidden>
        <path d="M256 0H0V234H288.9L311 195.722L256 0Z" pathLength="1" style={draw(FACE, 0.15)} />
      </svg>
      <DotsLoop aria-hidden className={inView ? "in" : ""}>
        <div className="dots-reveal">
          <img className="dots-base" src={dotsPattern} alt="" />
          <div className="dots-glints">
            <div className="dots-sweep" />
            <div className="dots-burst" />
          </div>
        </div>
      </DotsLoop>
      <div className="band-side" style={textFade}>
        <div className="band-label">
          {String(title).split(" ").map((w, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {w}
            </React.Fragment>
          ))}
        </div>
        <div className="band-line" />
        {scope && <div className="band-scope">{scope}</div>}
        {status && (
          <div className="band-status">
            <span className="band-dot" />
            {status}
          </div>
        )}
      </div>
      <div className="band-content">
        <h2 className="band-headline">{stripDashes(headline)}</h2>
        {children && (
          <p className="band-lede">
            {typeof children === "string" ? stripDashes(children) : children}
          </p>
        )}
      </div>
    </Band>
  );
}
